/**
 * useProfiles Hook
 * =================
 *
 * Hook for fetching and managing photo adjustment profiles.
 * Separates profiles into three categories:
 * - System profiles: Built-in profiles (is_system=true, no company)
 * - Company profiles: Shared by all company users (company set, not personal)
 * - Personal profiles: User's own profiles (is_personal=true)
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  fetchProfiles,
  createProfile,
  setDefaultProfile as apiSetDefaultProfile,
  importDstyleProfile as apiImportDstyleProfile,
} from '../api';

export function useProfiles() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadProfiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchProfiles();
      // Handle both paginated and non-paginated responses
      const results = data.results || data;
      setProfiles(Array.isArray(results) ? results : []);
    } catch (e) {
      setError(e.message || 'Failed to load profiles');
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  // Categorize profiles
  const categorizedProfiles = useMemo(() => {
    // System profiles: is_system=true (built-in, no company)
    const system = profiles.filter((p) => p.is_system);
    // Company profiles: has company, not personal (shared across company)
    const company = profiles.filter((p) => p.company && !p.is_personal);
    // Personal profiles: is_personal=true (user's own)
    const personal = profiles.filter((p) => p.is_personal);

    return { system, company, personal };
  }, [profiles]);

  // For backward compatibility
  const systemProfiles = categorizedProfiles.system;
  const companyProfiles = categorizedProfiles.company;
  const personalProfiles = categorizedProfiles.personal;

  const saveAsProfile = useCallback(async (name, adjustments, companyWide = false, profileType = 'delta') => {
    try {
      const newProfile = await createProfile(name, adjustments, companyWide, profileType);
      setProfiles((prev) => [...prev, newProfile]);
      return { ok: true, profile: newProfile };
    } catch (e) {
      return { ok: false, message: e.message || 'Failed to save profile' };
    }
  }, []);

  const setDefault = useCallback(async (profileId) => {
    try {
      await apiSetDefaultProfile(profileId);
      // Update local state to reflect the new default
      setProfiles((prev) =>
        prev.map((p) => ({
          ...p,
          is_default: p.id === profileId,
        }))
      );
      return { ok: true };
    } catch (e) {
      return { ok: false, message: e.message || 'Failed to set default profile' };
    }
  }, []);

  const importDstyle = useCallback(async (name, dstyleContent) => {
    try {
      const newProfile = await apiImportDstyleProfile(name, dstyleContent);
      setProfiles((prev) => [...prev, newProfile]);
      return { ok: true, profile: newProfile };
    } catch (e) {
      return { ok: false, message: e.message || 'Failed to import dstyle profile' };
    }
  }, []);

  return {
    profiles,
    systemProfiles,
    companyProfiles,
    personalProfiles,
    loading,
    error,
    refresh: loadProfiles,
    saveAsProfile,
    setDefault,
    importDstyle,
  };
}

export default useProfiles;
